import { defineStore } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "./useMyFetch";
import { userType, access } from "@utils/constants/userRoles";
import { identifyUser } from "@/lib/analytics";
import type { User, UserType, AccessKey } from "@/types";

export const useCurrentUserStore = defineStore("currentUser", () => {
  // state
  const user = ref<User | null>(null);

  // getters
  const getUserType = computed<UserType | null | undefined>(() => user.value?.user_type);
  const userAccountIds = computed(() => user.value?.account_ids);
  const isLoggedIn = computed(() => user.value !== null);
  const isAdmin = computed(
    () => isLoggedIn.value && [userType.ADMIN, userType.SUPER_ADMIN].includes(getUserType.value as string),
  );
  const isClient = computed(() => isLoggedIn.value && getUserType.value === userType.CLIENT);
  const isSuperAdmin = computed(() => isLoggedIn.value && getUserType.value === userType.SUPER_ADMIN);
  const isSale = computed(() => isLoggedIn.value && getUserType.value === userType.SALES);
  const isEpisto = computed(() => isAdmin.value || isSale.value);
  const withBrevoAccess = computed(() => isLoggedIn.value && user.value?.has_brevo_access);
  const getUser = computed(() => user.value);

  // Episto staff bypasses the accesses catalog; clients hold the union of the
  // accesses granted by their roles, as computed by the API.
  const accesses = computed<AccessKey[]>(() => user.value?.accesses ?? []);
  const hasAccess = (key: AccessKey) => isEpisto.value || accesses.value.includes(key);
  const hasAnyAccess = (keys: AccessKey[]) => isEpisto.value || keys.some((key) => accesses.value.includes(key));


  // Accesses held through an account role, as opposed to a role on a single
  // survey. Account-scoped features gate on these: a survey role never unlocks
  // creating a survey, importing data or managing report themes.
  const accountAccesses = computed<AccessKey[]>(() => user.value?.account_accesses ?? []);
  const hasAccountAccess = (key: AccessKey) => accountAccesses.value.includes(key);

  const canCreateSurvey = computed(() => hasAccountAccess(access.SURVEYS_WRITE as AccessKey));
  const canImportSurveyData = computed(() => hasAccountAccess(access.SURVEYS_WRITE as AccessKey));
  const canManageReportThemes = computed(() => hasAccountAccess(access.SURVEYS_ANALYZE as AccessKey));
  // Branding the chat pages a questionnaire is served through belongs to the
  // programming bucket, held at the account level.
  const canManageChatPages = computed(() => hasAccountAccess(access.SURVEYS_WRITE as AccessKey));
  // Managing an account's members: accounts.read is not enough, and a role held
  // on a single survey never grants it either.
  const canManageMembers = computed(() => hasAccountAccess(access.ACCOUNTS_MANAGE as AccessKey));

  // Held anywhere — a survey role is enough. Survey pages must gate on the
  // survey store's own accesses instead, never on these.
  const canWriteSurveys = computed(() => hasAccess(access.SURVEYS_WRITE as AccessKey));
  const canMonitorSurveys = computed(() => hasAccess(access.SURVEYS_MONITOR as AccessKey));
  const canAnalyzeSurveys = computed(() => hasAccess(access.SURVEYS_ANALYZE as AccessKey));

  // actions
  async function fetch() {
    const { data } = await useMyFetch("users/current").json<User>();
    user.value = data.value;
    if (user.value) identifyUser(user.value);
  }

  return {
    // state
    user,

    // getters
    getUserType,
    userAccountIds,
    isLoggedIn,
    isAdmin,
    isClient,
    isSuperAdmin,
    isSale,
    isEpisto,
    withBrevoAccess,
    getUser,
    accesses,
    accountAccesses,
    canCreateSurvey,
    canImportSurveyData,
    canManageReportThemes,
    canManageChatPages,
    canManageMembers,
    canWriteSurveys,
    canMonitorSurveys,
    canAnalyzeSurveys,

    // helpers
    hasAccess,
    hasAnyAccess,
    hasAccountAccess,

    // actions
    fetch,
  };
});
